import { useState } from 'react';

// How every Business Settings surface holds a field that is typed rather than
// clicked.
//
// There is no Save button in Settings (see SettingsPage.jsx). A radio, a
// checkbox and a select commit the moment they change, each a complete
// decision. A typed field cannot: "14" passes through "1" on the way in, and a
// studio should not briefly own a one-day hold, nor a document write per
// keystroke. So a typed field is held locally and committed when it is left.
// One module, so every panel guards an unchanged value, snaps back to what was
// stored, and commits on leave in the same way.
//
// Nothing here writes. Each hook calls the `commit` it was handed, and every
// call site hands it a patch naming only the field that changed: the provider
// merges a group patch one level deep (GROUPS in data/useSettings.jsx), so a
// whole group would carry that render's stale copy of its siblings and undo a
// change made a moment ago.

// The record holds null for "unset"; an input holds ''. One direction only —
// the normalisers in domain/studio.js turn '' back into null on the way in, so
// "absent" stays one value and not two.
export const asDraft = (v) => (v == null ? '' : String(v));

// A single-line field that should commit when Enter is pressed as well as when
// focus leaves. Opt-in rather than baked into the hooks below, because a
// textarea must not: Enter is a newline there, and a shared handler that blurred
// on it would make the studio address a one-line field.
export const blurOnEnter = (e) => { if (e.key === 'Enter') e.currentTarget.blur(); };

// What is in the box, following the record when the record moves.
//
// The draft follows `settled` when `settled` changes — an edit from another
// device, or the first snapshot arriving after the field mounted — adjusted
// during the render that sees the change, not from an effect (#429). An
// effect that set the draft could still be owed when the first keystroke
// arrives (a field mounted by a non-event render, such as the provider's first
// read): React flushes it at the start of the keystroke's render, the render
// after puts the box back, and the blur writes nothing.
// test/ui/committedDraft.test.jsx types into that gap.
//
// So a keystroke after the reset lands on top of it, and mounting resets
// nothing.
export function useDraft(settled) {
  const [draft, setDraft] = useState(settled);
  const [following, setFollowing] = useState(settled);
  if (following !== settled) {
    setFollowing(settled);
    setDraft(settled);
  }
  return [draft, setDraft];
}

// Both hooks return `{ field, commit }` rather than one flat object, so that
// `{...x.field}` puts nothing on the element that is not a DOM prop. Spreading
// a bare `commit` onto an <input> is a React warning and an attribute the
// browser does not understand.

// Free text — a studio name, an invoice footer, an email address.
//
// `suggestion` is a value to show when nothing is stored, not a value to store:
// the profile drawer offers the teacher's own name as the studio name, as MMS
// does. A field left exactly as it was offered is not a choice the teacher
// made, and tabbing past it must not name their studio.
export function useCommittedText(stored, commit, { suggestion = '' } = {}) {
  const settled = asDraft(stored) || suggestion;
  // Follows the record when it changes underneath (`useDraft`): a draft that
  // ignored it would shadow an edit made on a phone with whatever this browser
  // last showed.
  const [draft, setDraft] = useDraft(settled);

  const write = () => {
    // Two ways of being unchanged: still showing what was offered, or typed
    // back to what is stored. Neither is worth a write, and the first would
    // store a suggestion nobody accepted.
    if (draft === settled || draft === asDraft(stored)) return;
    commit(draft);
  };

  return {
    field: { value: draft, onChange: (e) => setDraft(e.target.value), onBlur: write },
    commit: write,
  };
}

// A typed field whose stored form is not a string: a number, a hex colour.
//
// `parse` turns what is in the box into what would be stored — through the
// domain's own normaliser, so the value the field shows after a blur is exactly
// the value the document holds. An empty box or "999" comes back clamped rather
// than sitting there looking accepted, and a `parse` that hands back `stored`
// is how a field rejects an edit and snaps back.
export function useCommittedValue(stored, parse, commit) {
  const settled = asDraft(stored);
  const [draft, setDraft] = useDraft(settled);

  const write = () => {
    const next = parse(draft);
    // Set unconditionally rather than only when it differs from `stored`: a
    // value that normalises back to what is already stored writes nothing, and
    // without this the box would keep showing the text it was clamped from.
    setDraft(asDraft(next));
    if (next !== stored) commit(next);
  };

  return {
    field: { value: draft, onChange: (e) => setDraft(e.target.value), onBlur: write },
    commit: write,
  };
}
